import React, { useEffect, useMemo, useRef } from 'react';
import TagTextField from '../../TagTextField';
import { resolveTagName } from '../../../utils/tagDisplay';
import { parseTokens } from '../../../utils/segmentTags';

// Tag-safe target editor. Used for segments that carry inline style tags
// (<1>…</1>): the Slate editor renders each tag as an atomic pill that can't
// be split or partially deleted, and the palette below lets the translator
// restore any tag that was dropped — click to insert at the caret, or drag
// onto the editor. Plain (tag-free) segments keep the simpler textarea path.

const TAG_LETTER = { b: 'B', strong: 'B', i: 'I', em: 'I', u: 'U' };

const pillLabel = (tagMap, tag, open) => {
    if (tag === 'br') return '↵';
    const name = resolveTagName(tagMap, tag);
    const base = (name && (TAG_LETTER[name.toLowerCase()] || name)) || tag;
    return open ? base : `/${base}`;
};

const pillTitle = (tagMap, tag, open) => {
    if (tag === 'br') return 'Line break — click or drag to insert';
    const name = resolveTagName(tagMap, tag);
    const which = open ? 'opening' : 'closing';
    return name ? `${name} ${which} tag — click or drag to insert` : `${which} tag ${tag}`;
};

export default function TargetEditor({
    value = '',
    onChange,
    dir = 'ltr',
    tagMap = {},
    source = '',
    onApprove,
    focusKey,
}) {
    const editorRef = useRef(null);

    // Refocus the editor when we move to a new segment, mirroring the
    // textarea's autofocus-on-index behaviour.
    useEffect(() => {
        if (editorRef.current) editorRef.current.focus();
    }, [focusKey]);

    // Which source tags are still missing from the target. Each source token
    // is matched against one target token of the same id+direction; leftovers
    // are "missing" and shown as actionable. Already-placed tags are dimmed.
    const palette = useMemo(() => {
        const remaining = {};
        parseTokens(value).forEach((t) => {
            const k = `${t.tag}:${t.open}`;
            remaining[k] = (remaining[k] || 0) + 1;
        });
        return parseTokens(source).map((t) => {
            const k = `${t.tag}:${t.open}`;
            const present = remaining[k] > 0;
            if (present) remaining[k] -= 1;
            return { ...t, present };
        });
    }, [source, value]);

    const handleKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            if (onApprove) onApprove();
        }
    };

    const missingCount = palette.filter((p) => !p.present).length;

    return (
        <div className="rs-tageditor">
            <TagTextField
                ref={editorRef}
                value={value}
                onChange={onChange}
                dir={dir}
                tagMap={tagMap}
                placeholder="Translate this segment…"
                onKeyDown={handleKeyDown}
                className="a-input"
                inlineBreaks
            />
            {palette.length > 0 && (
                <div className="tag-palette" role="group" aria-label="Insert formatting tags">
                    <span className="tag-palette-lbl">
                        {missingCount > 0 ? `${missingCount} tag${missingCount === 1 ? '' : 's'} to place` : 'Tags'}
                    </span>
                    {palette.map((p, i) => (
                        <button
                            key={`${p.tag}-${p.open ? 'o' : 'c'}-${i}`}
                            type="button"
                            className={`tag-chip ${p.tag === 'br' ? 'brk' : (p.open ? 'open' : 'close')} ${p.present ? 'present' : 'missing'}`}
                            title={pillTitle(tagMap, p.tag, p.open)}
                            draggable
                            onDragStart={(e) => {
                                e.dataTransfer.setData('text/tag', p.tag);
                                e.dataTransfer.setData('text/type', p.open ? 'open' : 'close');
                                if (p.tag === 'br') e.dataTransfer.setData('text/nl', p.nl ? '1' : '0');
                            }}
                            onClick={() => editorRef.current && editorRef.current.insertTag(p.tag, p.open, { nl: p.nl })}
                        >
                            {pillLabel(tagMap, p.tag, p.open)}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
